import React, { JSX } from "react";
import startCase from "lodash/startCase";
import { Box, Typography } from "@mui/material";
import {
  scaleFont,
  verifyShowTestCategory,
  getGender,
  createMarkup,
  AdvancedLabRangeHandler,
  advancedLabRangeIndicator,
  showCategory,
  showSubRow,
  addDefaultFormData,
  LabHeadersFlexBasis,
  readingPresent,
  isRadiologyOrCytology
} from "../../LabPrintFunctions";
import styles from "../../../Lab.module.css";
import { LabTest, LabTestRecordResultData } from "../../../../../interfaces/Lab";
import { EditableTableView } from "../../../../../components/EditableTable/EditableTable";
import hasOwnProperty from "../../../../../helpers/object";
import { removeCommasFromNumber } from "../../../../../helpers/number";
import { LabTestStatusTypes } from "../../../LabList";
import { useShowLabTestsWithoutReadingsInPrint } from "../../../../../hooks/useShowLabTestsWithoutReadingsInPrint";

const EXCLUDE_GROUP_CATEGORY = ["Special", "undefined"]; // add group category here to hide

// custom element
const CustomEl = ({ data }) => (
  <div dangerouslySetInnerHTML={createMarkup(data)} className={styles.additionalData} />
);

// sub row
interface SubRowProps {
  pl: number;
  lt: LabTest;
  children?: React.ReactNode;
  hasSubTests?: boolean;
  gender: "1" | "2";
  dob: string;
  showMethods: boolean;
  showRanges: boolean;
  showUnit: boolean;
  showReading: boolean;
  showRangeFlag: boolean;
  showLabTestsWithoutReadingsInPrint?: boolean;
  type?: string;
}

export const SubRow = ({
  pl,
  lt,
  children = <></>,
  hasSubTests = false,
  gender,
  showMethods,
  dob,
  showRanges,
  showUnit,
  showReading,
  showRangeFlag,
  showLabTestsWithoutReadingsInPrint = false,
  type
}: SubRowProps): JSX.Element => {
  const clientGender = getGender(lt, gender);
  const commonStyles = { fontSize: scaleFont("0.33cm", 1), wordBreak: "break-all" };

  const rangeSymbol = advancedLabRangeIndicator(
    Number(removeCommasFromNumber(lt.formData.reading)),
    lt.ranges,
    dob,
    gender
  );

  const isRadiology = isRadiologyOrCytology(lt.type || type);

  return (
    <>
      {showSubRow(hasSubTests, lt, showLabTestsWithoutReadingsInPrint) &&
        (isRadiology ? (
          <Box py="2px" style={{ paddingLeft: `${pl}cm` }}>
            <Typography
              style={{
                fontSize: scaleFont("0.33cm", 1),
                paddingRight: "0.6cm",
                ...(hasSubTests && { fontWeight: "bold" })
              }}
            >
              {lt.name}
            </Typography>
            {showReading && lt?.formData?.reading && (
              <Typography
                component="div"
                style={{
                  ...commonStyles,
                  whiteSpace: "pre-wrap",
                  paddingTop: "2px"
                }}
              >
                {lt.formData.reading.includes("<") ? (
                  <CustomEl data={lt.formData.reading} />
                ) : (
                  lt.formData.reading
                )}
              </Typography>
            )}
          </Box>
        ) : (
          <Box display="flex" py="2px">
            <Box flexBasis="7cm" flexGrow="0.4" flexShrink="0.4" style={{ paddingLeft: `${pl}cm` }}>
              <Typography
                style={{
                  fontSize: scaleFont("0.33cm", 1),
                  paddingRight: "0.6cm",
                  ...(hasSubTests && { fontWeight: "bold" })
                }}
              >
                {lt.name}
              </Typography>
            </Box>
            {showReading && (
              <Typography
                style={{
                  ...commonStyles,
                  fontWeight: rangeSymbol ? 900 : 500,
                  marginRight: "0.5cm"
                }}
                flexBasis="5cm"
                flexGrow="0.15"
                flexShrink="0.15"
              >
                {lt?.formData.reading || null}
              </Typography>
            )}
            {showRangeFlag && (
              <Typography style={commonStyles} flexBasis="1.5cm" flexGrow="0.15" flexShrink="0.15">
                {rangeSymbol.highSymbol || rangeSymbol.lowSymbol || null}
              </Typography>
            )}
            {showUnit && (
              <Typography style={commonStyles} flexBasis="1.8cm" flexGrow="0.15" flexShrink="0.15">
                {!(lt.subTests?.length > 0) && hasOwnProperty(lt, "unit") && lt.unit}
              </Typography>
            )}
            {showRanges && (
              <Box flexBasis="3.7cm" flexGrow="0.15" flexShrink="0.15">
                <AdvancedLabRangeHandler
                  range={lt.ranges}
                  gender={Number(clientGender)}
                  dob={dob}
                />
              </Box>
            )}
            {showMethods && (
              <Typography flexBasis="3cm" flexGrow="0.15" flexShrink="0.15" fontSize={12}>
                {lt.methods}
              </Typography>
            )}
          </Box>
        ))}

      {children}

      {lt.subTests &&
        lt.subTests?.map((sT) => (
          <SubRow
            key={sT.id}
            pl={pl + 0.5}
            lt={addDefaultFormData(sT)}
            gender={gender}
            showMethods={showMethods}
            dob={dob}
            hasSubTests={Boolean(sT.subTests?.length)}
            showRanges={showRanges}
            showUnit={showUnit}
            showReading={showReading}
            showRangeFlag={showRangeFlag}
            type={sT.type || type}
          />
        ))}
    </>
  );
};

// test name with sample info
export const TestNameWithSampleInfo = ({
  test,
  sampleInfo,
  collectedBy
}: {
  test: {
    name: string;
    style?: React.CSSProperties;
  };
  sampleInfo: {
    sampleType?: string;
    style?: React.CSSProperties;
  };
  collectedBy: {
    name?: string;
    style?: React.CSSProperties;
  };
}): JSX.Element => (
  <Box sx={{ display: "flex", flexDirection: "column" }}>
    {test.name && (
      <Typography sx={{ fontWeight: 800, ...(test.style || {}) }}>
        {startCase(test.name)}
      </Typography>
    )}
    <Typography
      component="span"
      sx={{
        ...(sampleInfo.style || {
          fontSize: scaleFont("0.33cm", 0.8),
          fontStyle: "italic",
          whiteSpace: "break-spaces"
        })
      }}
    >
      {sampleInfo.sampleType && `Sample Type: ${sampleInfo.sampleType}`}
      {collectedBy.name &&
        `${sampleInfo.sampleType ? "," : "Sample"} Taken by: ${collectedBy.name}`}
    </Typography>
  </Box>
);

// row
interface RowProps {
  item: LabTestRecordResultData;
  gender: "1" | "2";
  showAdditionalLabData: boolean;
  showMethods: boolean;
  dob: string;
  showRanges: boolean;
  showUnit: boolean;
  showReading: boolean;
  showRangeFlag: boolean;
  showSampleCollectedBy: boolean;
  showLabTestsWithoutReadingsInPrint?: boolean;
  type?: string;
}

export const Row = ({
  item,
  gender,
  showAdditionalLabData,
  showMethods,
  dob,
  showRanges,
  showUnit,
  showReading,
  showRangeFlag,
  showSampleCollectedBy,
  showLabTestsWithoutReadingsInPrint = false,
  type
}: RowProps): JSX.Element => {
  const showTestCategory = verifyShowTestCategory(item);

  return (
    <Box>
      <TestNameWithSampleInfo
        test={{
          name: showTestCategory ? item.name : "",
          style: {
            fontSize: scaleFont("0.33cm", 1)
          }
        }}
        sampleInfo={{ sampleType: item.sampleInfo?.sampleType }}
        collectedBy={{ name: showSampleCollectedBy ? item.collectedBy?.name : "" }}
      />

      {item.labTests?.map((lt) => (
        <SubRow
          key={lt.id}
          pl={showTestCategory ? 0.5 : 0}
          lt={addDefaultFormData(lt)}
          hasSubTests={Boolean(lt.subTests?.length)}
          gender={gender}
          showMethods={showMethods}
          dob={dob}
          showRanges={showRanges}
          showUnit={showUnit}
          showReading={showReading}
          showRangeFlag={showRangeFlag}
          showLabTestsWithoutReadingsInPrint={showLabTestsWithoutReadingsInPrint}
          type={lt.type || item.type || type}
        />
      ))}

      {showAdditionalLabData && item?.additionalTestData && (
        <div style={{ whiteSpace: "pre-wrap" }}>
          <CustomEl data={item?.additionalTestData} />
        </div>
      )}

      <div>
        {item?.interpretationTemplate && (
          <EditableTableView tableState={item?.interpretationTemplate} />
        )}
      </div>
    </Box>
  );
};

// data headers
export const DataHeaders = ({
  headers = ["Test"],
  showMethods,
  showRanges,
  showUnit,
  showReading,
  type,
  readingsPresent,
  showRangeFlag
}: {
  headers?: string[];
  showMethods: boolean;
  showRanges: boolean;
  showUnit: boolean;
  showReading: boolean;
  type: string;
  readingsPresent: boolean;
  showRangeFlag: boolean;
}): JSX.Element => {
  const isRadiology = isRadiologyOrCytology(type);

  function handleHeaders(h = headers) {
    const headings = [...h];
    if (showReading) {
      headings.push("Result");
    }
    if (showRangeFlag) {
      headings.push("Flag");
    }
    if (showUnit) {
      headings.push("Unit");
    }
    if (showRanges) {
      headings.push("Range");
    }
    if (showMethods) {
      headings.push("Methods");
    }
    if (isRadiology) {
      headings.splice(1, headings.length);
    }
    return headings;
  }

  const getFlexGrow = (index: number) => {
    if (isRadiology) return 1;
    return index === 0 ? 0.4 : 0.15;
  };

  return (
    <>
      {readingsPresent && (
        <Box display="flex" borderBottom="1px solid lightgrey" pb="4px">
          {handleHeaders().map((colHeader, i) => (
            <Typography
              key={colHeader}
              style={{
                flexGrow: getFlexGrow(i),
                flexShrink: i === 0 ? 0.4 : 0.15,
                flexBasis: isRadiology ? "100%" : LabHeadersFlexBasis[colHeader] || "7cm",
                fontSize: scaleFont("0.27cm", 1.2),
                textTransform: "uppercase",
                fontWeight: 600
              }}
            >
              {colHeader}
            </Typography>
          ))}
        </Box>
      )}
    </>
  );
};

// core data
interface CoreDataProps {
  labData: Record<string, any>;
  showAdditionalLabData: boolean;
  showMethods: boolean;
  showRanges: boolean;
  showUnit: boolean;
  showReading: boolean;
  showRangeFlag: boolean;
  showSampleCollectedBy: boolean;
  showLabTestsWithoutReadingsInPrint?: boolean;
}

export const CoreData = ({
  labData,
  showAdditionalLabData,
  showMethods,
  showRanges,
  showUnit,
  showReading,
  showRangeFlag,
  showSampleCollectedBy,
  showLabTestsWithoutReadingsInPrint = false
}: CoreDataProps): JSX.Element => {
  const { data } = labData.results;
  const { gender, dob } = labData.client;
  const showRowColumn = !isRadiologyOrCytology(labData.type);

  return (
    <Box py="0.1cm">
      {Object.keys(data).map(
        (item) =>
          showCategory(
            data[item],
            labData?.status === LabTestStatusTypes.ORDERED || showLabTestsWithoutReadingsInPrint
          ) && (
            <Box key={item}>
              {!EXCLUDE_GROUP_CATEGORY.includes(item) && (
                <Typography
                  style={{
                    textTransform: "uppercase",
                    fontWeight: "bold",
                    textDecoration: "underline"
                  }}
                >
                  {item}
                </Typography>
              )}

              <Box ml={!EXCLUDE_GROUP_CATEGORY.includes(item) ? "8px" : "0px"}>
                {data[item].map((test: LabTestRecordResultData) => (
                  <Row
                    key={test.name}
                    item={test}
                    gender={gender}
                    showAdditionalLabData={showAdditionalLabData}
                    showMethods={showRowColumn && showMethods}
                    dob={dob}
                    showRanges={showRowColumn && showRanges}
                    showUnit={showRowColumn && showUnit}
                    showReading={showReading}
                    showRangeFlag={showRangeFlag}
                    showSampleCollectedBy={showSampleCollectedBy}
                    showLabTestsWithoutReadingsInPrint={showLabTestsWithoutReadingsInPrint}
                    type={test.type || labData.type}
                  />
                ))}
              </Box>
            </Box>
          )
      )}
    </Box>
  );
};

// data comment
export const DataComment = ({
  comment,
  sxProp
}: {
  comment: string;
  sxProp?: Record<string, any>;
}): JSX.Element => (
  <Box display="flex" width="100%">
    <Typography
      sx={{ fontSize: scaleFont("0.33cm", 1), whiteSpace: "pre-wrap", width: "100%", ...sxProp }}
    >
      <CustomEl data={comment} />
    </Typography>
  </Box>
);

export interface LabPrintCoreDataProps {
  labData: Record<string, any>;
  showAdditionalLabData: boolean;
  showMethods: boolean;
  showRanges: boolean;
  showUnit: boolean;
  showReading: boolean;
  showRangeFlag: boolean;
  showSampleCollectedBy: boolean;
}

// lab print core data
const LabPrintCoreData = ({
  labData,
  showAdditionalLabData,
  showMethods = false,
  showRanges = false,
  showUnit = false,
  showReading = false,
  showRangeFlag = false,
  showSampleCollectedBy = false
}: LabPrintCoreDataProps): JSX.Element => {
  const { showLabTestsWithoutReadingsInPrint } = useShowLabTestsWithoutReadingsInPrint();

  const readingContains = readingPresent(
    labData,
    labData?.status === LabTestStatusTypes.ORDERED || showLabTestsWithoutReadingsInPrint
  );

  return (
    <div>
      <Box borderBottom="1px solid lightgrey">
        {labData?.status !== LabTestStatusTypes.ORDERED && (
          <DataHeaders
            showMethods={showMethods}
            type={labData.type}
            showRanges={showRanges}
            showUnit={showUnit}
            showReading={showReading}
            readingsPresent={readingContains}
            showRangeFlag={showRangeFlag}
          />
        )}

        <CoreData
          labData={labData}
          showAdditionalLabData={showAdditionalLabData}
          showMethods={showMethods}
          showRanges={showRanges}
          showUnit={showUnit}
          showReading={showReading}
          showRangeFlag={showRangeFlag}
          showSampleCollectedBy={showSampleCollectedBy}
          showLabTestsWithoutReadingsInPrint={showLabTestsWithoutReadingsInPrint}
        />
      </Box>

      {labData.results.showComment && labData.results.comment && (
        <Box mt="16px">
          <DataComment comment={labData.results.comment} />
        </Box>
      )}
    </div>
  );
};

export default LabPrintCoreData;
